import type React from "react";
import { useMemo, useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import queryString from "query-string";
import styled from "styled-components";

import type { IDynamicFilterRule } from "@similarweb/pro-logic-components";
import Sentry from "@similarweb/sentry-logger";

import { tableActionsCreator } from "app/actions/tableActions";
import type { IData } from "app/common/keyword-research/website-keywords/websiteKeywordsPageTypes";
import { safeJsonParse } from "app/UtilitiesAndConstants/UtilityFunctions/safeJsonParse";
import { apiHelper } from "app/common/services/apiHelper";
import { chosenSites } from "app/common/services/chosenSites";
import {
    createComparePositionFilter,
    createCpcFilter,
    createPositionFilter,
    createVolumeFilter,
    getRangeFilterQueryParamValue,
} from "pro-features/components/filtersPanel/src/RangeFilterUtilityFunctionsV2";
import { SWReactTableWrapperBox } from "app/components/React/Table/SWReactTableGhostLoader";
import { SWReactTableWrapperWithSelection } from "app/components/React/Table/SWReactTableWrapperWithSelection";
import AddTableRowsKeywordsToGroupUtility from "app/pages/keyword-analysis/AddTableRowsKeywordsToGroupUtility";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import DurationService, { BasicDurations } from "app/services/DurationService";
import { allTrackers } from "app/services/track/track";

import { transformData } from "./rankingDistributionTable.utils";
import { RankingDistributionTableTop } from "./RankingDistributionTableTop";
import { getColumnsConfig } from "./RanknigDistributionColumns";

const Container = styled.div`
    ${SWReactTableWrapperBox} {
        border-radius: 0;
        box-shadow: none;
    }
`;

const DEFAULT_SORT_SINGLE = "position0";
const DEFAULT_SORT_COMPARE = "KwVolume";
const DEFAULT_SORT_COMPARE_WINDOW = "KwWindowVolume";

const getDefaultSortField = (isCompare: boolean, isWindow: boolean) => {
    if (!isCompare) {
        return DEFAULT_SORT_SINGLE;
    }
    return isWindow ? DEFAULT_SORT_COMPARE_WINDOW : DEFAULT_SORT_COMPARE;
};

export const RankingDistributionMain: React.FC<any> = () => {
    const { params } = useCurrentStateAndParams();
    const { multiIncludeExcludeUrl, multiIncludeExcludeKeywords, duration } = params;
    const isWindow = duration === BasicDurations.LAST_TWENTY_EIGHT_DAYS;
    const isCompare = params.key.split(",").length > 1;
    const [dataTimeOut, setDataTimeOut] = useState<boolean>(false);
    const numberOfMonths = DurationService.getMonthsFromDuration(params.duration);
    const [noData, setNoData] = useState<boolean>(false);

    // need to save the table data, since the columns config is calculated based on the data
    const [tableData, setTableData] = useState<IData>({});
    const [columnSort, setColumnSort] = useState({
        field: getDefaultSortField(isCompare, isWindow),
        sortDirection: "desc",
    });

    const handleDataTimeOut = () => {
        setDataTimeOut(true);
        const domains = params?.key.split(",") || [];
        allTrackers.dispatchTrackEvent("Timeout notification", "open", "no-results", {
            domains: domains,
            numberOfDomains: domains.length,
            numberOfMonths: numberOfMonths,
            failedApi: SERVER_API,
            filters: `Country: ${params?.country}, Duration: ${params?.duration}, Device: ${params?.webSource}`,
        });
    };
    const onDataError = () => {
        handleDataTimeOut();
        setNoData(true);
    };
    const getDataCallback = (data) => {
        setTableData(data);
    };

    const SERVER_API = `/widgetApi/RankDistributionV2`;

    const initialFilters: any = {
        ...params,
        keys: params.key,
        IncludeBranded: params.IncludeBranded === "true",
        IncludeNoneBranded: params.IncludeNoneBranded === "true",
        includeSubDomains: params.isWWW === "*",
        page: 0,
        pageSize: 100,
        timeGranularity: "Monthly",
        sort: columnSort.field,
        asc: columnSort.sortDirection !== "desc",
        serp: params.serp?.split(","),
        pageFilterJson: params.pageFilter,
        HideKeywordsWithoutPosition: true,
    };
    delete initialFilters.key;
    delete initialFilters.isWWW;
    delete initialFilters.selectedTab;
    delete initialFilters.multiIncludeExcludeUrl;
    delete initialFilters.multiIncludeExcludeKeywords;

    const columns = useMemo(
        () =>
            getColumnsConfig({
                hasKwaPermission: true,
                domains: safeJsonParse(params.pageFilter, []),
                sortedColumn: initialFilters.sort,
                sortDirection: initialFilters.asc ? "asc" : "desc",
                currentMonth: initialFilters.duration.split("-")[0],
                duration: params.duration,
                isWindow: params.duration === BasicDurations.LAST_TWENTY_EIGHT_DAYS,
            }),
        [tableData],
    );

    const getRequestBody = () => {
        try {
            const url: IDynamicFilterRule[] = multiIncludeExcludeUrl ? JSON.parse(multiIncludeExcludeUrl) : [];
            const keywords: IDynamicFilterRule[] = multiIncludeExcludeKeywords
                ? JSON.parse(multiIncludeExcludeKeywords)
                : [];
            return [
                ...url.map((rule) => ({ ...rule, field: "url" })),
                ...keywords.map((rule) => ({ ...rule, field: "keyword" })),
            ];
        } catch (e) {
            Sentry.error("Error making the post request:", e);
            return [];
        }
    };
    const requestBody = getRequestBody();

    const dataParamsAdapter = (params, table = true) => {
        const { volumeFromValue, volumeToValue, cpcFromValue, cpcToValue, positionTrends, ranking: position } = params;

        const positionInArray = position?.split(",");
        let fromPosition, toPosition, positionName, positionValue;
        if (positionInArray?.length === 1) {
            [positionName, positionValue] = position.split(":");
            [fromPosition, toPosition] = positionValue.split("-");
        }
        // Transform positionTrends from domain:trend format to competitor_index_position_trend:trend format
        if (positionTrends) {
            const sites = safeJsonParse<{ url: string }[]>(params.pageFilter, []);
            const domainToIndexMap = sites.reduce(
                (acc, site, index) => {
                    acc[site.url] = index;
                    return acc;
                },
                {} as Record<string, number>,
            );

            const trendsArray = positionTrends.split(",");
            const transformedTrends = trendsArray
                .map((trendItem) => {
                    const [domain, trend] = trendItem.split(":");
                    const competitorIndex = domainToIndexMap[domain];
                    if (competitorIndex !== undefined) {
                        return `competitor_${competitorIndex}_position_trend:${trend}`;
                    }
                    return null;
                })
                .filter(Boolean);

            if (transformedTrends.length > 0) {
                params.positionTrends = transformedTrends.join(",");
            } else {
                delete params.positionTrends;
            }
        }

        params.rangefilter = getRangeFilterQueryParamValue([
            createVolumeFilter(volumeFromValue, volumeToValue, "volume"),
            createCpcFilter(cpcFromValue, cpcToValue, "cpc"),
            ...(positionInArray?.length > 1
                ? createComparePositionFilter(position, params.pageFilter)
                : [createPositionFilter(fromPosition, toPosition, positionName, params.pageFilter)]),
        ]);
        !params.rangefilter && delete params.rangefilter;
        delete params.volumeFromValue;
        delete params.volumeToValue;
        delete params.cpcFromValue;
        delete params.cpcToValue;
        delete params.ranking;
        delete params.pageFilter;

        //align page
        if (typeof params.page == "number" && table) {
            params.page += 1;
        }

        delete params.orderBy;

        return apiHelper.transformParamsForAPI(params);
    };

    const createTopComponentCustomProps = () => {
        let topComponentProps: any = {
            chosenItems: chosenSites.sitelistForLegend(),
            durationObject: DurationService.getDurationData(params.duration),
            excelLink: `${SERVER_API}/Excel?${queryString.stringify(
                dataParamsAdapter({ ...initialFilters, page: null }),
            )}`,
            noData: tableData?.Data?.Records?.length === 0,
            tableData,
            dataParamsAdapter,
            requestBody,
            initialFilters,
        };
        if (tableData.Header) {
            topComponentProps = {
                ...topComponentProps,
                // headerData: tableData.Header,
            };
        }
        return topComponentProps;
    };

    return (
        <Container>
            <SWReactTableWrapperWithSelection
                tableSelectionKey="RankingDistribution_Table"
                tableSelectionProperty="Keyword"
                serverApi={`${SERVER_API}/Table`}
                tableColumns={columns}
                totalRecordsField="TotalCount"
                onDataError={onDataError}
                rowsPerPage={100}
                onSort={setColumnSort}
                getDataCallback={getDataCallback}
                transformData={transformData(params.duration)}
                recordsField="Data"
                requestBody={requestBody}
                tableOptions={{
                    aboveHeaderComponents: [
                        <AddTableRowsKeywordsToGroupUtility
                            key="1"
                            clearAllSelectedRows={
                                tableActionsCreator("RankingDistribution_Table", "SearchTerm").clearAllSelectedRows
                            }
                            stateKey="RankingDistribution_Table"
                        />,
                    ],
                    metric: "RankingDistribution_Table",
                    onSerpIconClick: (serp) => {
                        proNavigator.applyUpdateParams(
                            {
                                serp: serp.id,
                            },
                            { reload: true },
                        );
                    },
                }}
                initialFilters={{ ...initialFilters }}
                dataParamsAdapter={dataParamsAdapter}
            >
                {(topComponentProps) => {
                    return (
                        <RankingDistributionTableTop
                            {...topComponentProps}
                            {...createTopComponentCustomProps()}
                        />
                    );
                }}
            </SWReactTableWrapperWithSelection>
        </Container>
    );
};
